import { Box, useToken } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { IoMdDocument } from 'react-icons/io';
import { IoEyeOutline } from 'react-icons/io5';
import { MdEdit, MdSupportAgent } from 'react-icons/md';
import { RxHamburgerMenu } from 'react-icons/rx';
import { SiVoidlinux } from 'react-icons/si';
import { TiDocumentText } from 'react-icons/ti';
import { useNavigate } from 'react-router';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import Show from '@/components/Molecules/Show';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import DataTableActionMenu from '@/components/Organisms/DataTable/DataTableActionMenu';
import BenefitVerificationForm, {
	DemoBVForm,
} from '@/components/Templates/BenefitVerification/BenefitVerificationForm';
import type { DemoBVFormHandle } from '@/components/Templates/BenefitVerification/BenefitVerificationForm/DemoBVForm';
import IVRViewModal, {
	DemoIVRViewModal,
} from '@/components/Templates/BenefitVerification/IVRViewModal';
import PRDocumentViewModal from '@/components/Templates/BenefitVerification/PRDocumentViewModal';
import Preview from '@/components/Templates/BenefitVerification/Steps/Preview';
import {
	DemoOrderRequestForm,
	OrderRequestForm,
} from '@/components/Templates/Requests/OrderRequestForm';
import { SupportTicketForm } from '@/components/Templates/SupportTickets/SupportTicketForm';
import {
	useGetBenefitVerifications,
	useGetBenefitVerificationsProducts,
	useGetSingleBV,
	useVoidBenefitVerification,
} from '@/hooks/service-hooks/benefitsVerification';
import { useGetCommissionableProducts } from '@/hooks/service-hooks/orderReturns';
import { useGetBvProducts, useGetProductsWithSizesByClinics } from '@/hooks/service-hooks/products';
import { useBVActions } from '@/hooks/tutorialsHooks/useBVActions';
import { useBVTutorial } from '@/hooks/tutorialsHooks/useBVTutorial';
import { useTutorialActive } from '@/hooks/tutorialsHooks/useTutorialActive';
import { useAppTour } from '@/hooks/useAppTour';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import usePermission from '@/hooks/usePermission';
import { getBenefitVerifications } from '@/services/benefitsVerification';
import { selectUser, useAuthStore } from '@/store/authStore';
import type {
	BenefitVerification,
	IVRViewModalData,
	PriorAuth,
} from '@/types/benefitsVerification';
import type { BvProduct } from '@/types/product';
import type { FilterType } from '@/types/table';
import {
	extractDocumentName,
	formatDate,
	transformBVRecordToPreview,
} from '@/utils';
import { trackCoreClick } from '@/utils/analytics';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { buildClinicKey, buildDocumentsUrl } from '@/utils/navigationItems';
import { generateMockBVData } from '@/utils/tutorials/mockData';
import {
	columns,
	getBVStatus,
	getBvStatuses,
	getProductDisplay,
	STATUS_SUB_ACTIONS_MAP,
	shouldShowAction,
	statusesFilterOptions,
} from './bv';

const BenefitVerificationTable = () => {
	const [brand600] = useToken('colors', ['brand.600']);
	// Shared clinic filter helpers
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(
		'bv._ta_clinic_value',
	);
	const [products, setProducts] = useState<string[]>([]);

	const { hasPermission } = usePermission();
	const user = useAuthStore(selectUser);
	const userPort = user?.userInformation?.portsAvailable;
	const userRole = user?.userInformation?.role;

	const [modalInformation, setModalInformation] = useState<{
		mode: string;
		data: string | null;
		size: string;
		step?: number | null;
	} | null>({
		mode: '',
		data: null,
		size: 'xl',
		step: null,
	});

	const [selectedBV, setSelectedBV] = useState<{
		action:
		| 'void'
		| 'ivr'
		| 'prDocument'
		| 'bvCreateOrder'
		| 'details'
		| 'supportTicket'
		| null;
		data: string | null | Record<string, SelectItem> | IVRViewModalData;
		bvIdFull?: string; // For support ticket subject
	}>({
		action: null,
		data: null,
	});

	const [isFormDirty, setIsFormDirty] = useState(false);
	const [benefitVerificationExitDraftAction, setBenefitVerificationExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);
	const [isSupportTicketFormDirty, setIsSupportTicketFormDirty] =
		useState(false);
	const [supportTicketExitDraftAction, setSupportTicketExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);
	const navigate = useNavigate();

	// Fetch products with sizes for all clinics to have the data ready for modals (instead of fetching on demand when opening a modal, which causes a delay)
	const { data: clinicProductsMap, isLoading: isLoadingProductsWithSizesByClinics } = useGetProductsWithSizesByClinics({
		clinicIds: defaultClinicIds,
		userPort: userPort || 'WoundCare+',
		enabled: defaultClinicIds.length > 0,
	});

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: defaultClinicIds.length > 0,
		status: '',
		globalFilters: JSON.stringify(
			defaultClinicIds.length
				? [
					{
						key: 'bv._ta_clinic_value',
						values: defaultClinicIds,
					},
				]
				: [],
		),
		sort: '',
	});

	const { data, isFetching, refetch } = useGetBenefitVerifications(filters);
	const { data: commissionableProducts } = useGetCommissionableProducts();
	const { data: productsData } = useGetBenefitVerificationsProducts({
		...filters,
		enabled: products.length === 0,
	});

	const { isBVTutorialActive: isDemoBVTutorialActive } = useTutorialActive();

	// Declare refs before using them in hooks
	const [
		benefitVerificationTableRef,
		addButtonBenefitVerificationTableRef,
		exportButtonBenefitVerificationTableRef,
	] = [
			useRef<HTMLDivElement | null>(null),
			useRef<HTMLButtonElement | null>(null),
			useRef<HTMLButtonElement | null>(null),
		];

	// Demo modal state (for tutorial)
	const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
	const demoFormRef = useRef<DemoBVFormHandle | null>(null);

	// Use the BV Tutorial hook to manage all tutorial logic
	const {
		shouldOpenFirstRowMenu,
		shouldOpenSecondRowMenu,
		isDemoIVRModalOpen,
		isDemoOrderNowModalOpen,
		setIsDemoIVRModalOpen,
		setIsDemoOrderNowModalOpen,
		mockedActions,
	} = useBVTutorial({
		benefitVerificationTableRef,
		onCloseDemoBVModal: () => {
			setIsDemoModalOpen(false);
		},
	});

	// Use BV Actions hook to handle tutorial actions
	useBVActions({
		openCreateBVModal: () => {
			setIsDemoModalOpen(true);
		},
		goToBVStep: (step: number) => {
			demoFormRef.current?.goToStep(step);
		},
		addWound: () => {
			demoFormRef.current?.addWound();
		},
		removeWound: () => {
			demoFormRef.current?.removeWound();
		},
	});

	useEffect(() => {
		if (
			productsData?.items?.productData &&
			productsData.items.productData.length &&
			products.length === 0
		) {
			setProducts(productsData.items.productData);
		}
	}, [productsData]);

	const { mutateAsync: voidBV, isLoading: isVoidingBV } =
		useVoidBenefitVerification(() => {
			setSelectedBV({ action: null, data: null });
		});

	const {
		mutate: getBVDetails,
		isLoading: isLoadingBVDetails,
		data: bvDetails,
	} = useGetSingleBV();

	const { data: bvProducts } = useGetBvProducts({
		clinicId: bvDetails?._ta_clinic_value || '',
		userPort: 'WoundCare+',
		enabled: !!bvDetails?._ta_clinic_value,
	});


	const accountProducts = useMemo(() => {
		if (bvDetails?.priorAuths && bvDetails?._ta_clinic_value) {
			return (
				bvDetails?.priorAuths?.map((product) => ({
					label: product.ta_name,
					value: String(product.ta_priorauthid) as unknown as number,
					qCode: product.ta_priorauthid,
				})) || []
			);
		}
		return [];
	}, [bvProducts, bvDetails?._ta_clinic_value]);

	useAppTour({
		elements: {
			benefitVerificationTableRef: benefitVerificationTableRef.current,
			addButtonBenefitVerificationTableRef:
				addButtonBenefitVerificationTableRef.current,
			exportButtonBenefitVerificationTableRef:
				exportButtonBenefitVerificationTableRef.current,
			BVAddButtonBenefitVerificationTableRef:
				addButtonBenefitVerificationTableRef.current,
			BVExportIconRef: exportButtonBenefitVerificationTableRef.current,
		},
	});

	const navigateToDoc = (item: ColumnData) => {
		const url = String(item.ta_portalbvupload) || '';
		if (url && item?.clinic) {
			const clinic = item?.clinic as SelectItem;
			const documentsUrl = buildDocumentsUrl(
				url,
				String(clinic.value),
				clinic.label,
			);
			if (documentsUrl) {
				navigate(documentsUrl);
			}
		}
	};

	const getCommonActions = useCallback((isSubRow: boolean = false) => [
		{
			label: 'View IVR',
			icon: <IoEyeOutline />,
			actionFn: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					mockedActions.handleViewIVR(item);
					return;
				}

				const priorAuthId = (item.auth as unknown as PriorAuth)
					?.ta_priorauthid as string;
				const patient = item.patient as unknown as
					| {
						fullname?: string;
					}
					| undefined;
				const patientName = patient?.fullname || '';
				const ta_manufacturerbvupload = (item.auth as unknown as PriorAuth)
					?.ta_manufacturerbvupload as string | null;
				const ta_name = (item.auth as unknown as PriorAuth)?.ta_name as string;

				if (priorAuthId) {
					setSelectedBV({
						action: 'ivr',
						data: {
							priorAuthId,
							patientName,
							ta_manufacturerbvupload,
							priorAuthName: ta_name,
						},
					});
				} else {
					toast.error('IVR document not available');
				}
			},
			show: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					const status = (item.status || 'Mixed') as string;
					const allowedStatuses = ['Approved', 'Order Placed', 'Auth Approved'];
					if (
						allowedStatuses.includes(status) &&
						!isSubRow &&
						!item.subRows?.length
					) {
						return true;
					}
				}
				return shouldShowAction({
					item,
					commissionableProducts,
					actionLabel: 'View IVR',
					isSubRow,

					clinicProducts: clinicProductsMap,
				});
			},
		},
		{
			label: 'Void Now',
			icon: <SiVoidlinux />,
			actionFn: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					mockedActions.handleVoidNow(item);
					return;
				}

				const bvId =
					(item.ta_benefitsverificationid as string) || (item.id as string);
				if (bvId) {
					trackCoreClick('void_bv_clicked', {
						source_module: 'benefits_verification_table',
						bv_id: bvId,
					});
					setSelectedBV({ action: 'void', data: bvId });
				}
			},
			show: (item: ColumnData) => {
				// Hide for port SalesRep and role Rep
				if (userPort === 'SalesRep' && userRole === 'Rep') {
					return false;
				}
				return shouldShowAction({
					item,
					commissionableProducts,
					actionLabel: 'Void Now',
					isSubRow,
					clinicProducts: clinicProductsMap,
				});
			},
		},
		{
			label: 'Order Now',
			icon: <TiDocumentText />,
			actionFn: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					mockedActions.handleOrderNow(item);
					return;
				}

				const patient = item.patient as unknown as {
					fullname: string;
					contactid: string;
				};
				const auth = item.auth as unknown as {
					ta_name: string;
					ta_priorauthid: string;
				};
				trackCoreClick('order_now_clicked', {
					source_module: 'benefits_verification',
					bv_id:
						(item.ta_benefitsverificationid as string) || (item.id as string),
					patient_id: patient.contactid,
					prior_auth_id: auth.ta_priorauthid,
				});
				setSelectedBV({
					action: 'bvCreateOrder',
					data: {
						patient: {
							label: patient.fullname,
							value: patient.contactid,
						} as SelectItem,
						auth: {
							label: auth.ta_name,
							value: auth.ta_priorauthid,
						} as SelectItem,
					},
				});
			},
			show: (item: ColumnData) => {
				// Check permission first
				if (!hasPermission(['Orders:Create'])) {
					return false;
				}
				if (item?.orderRequest) { // If an order has already been placed
					return false;
				}
				if (isDemoBVTutorialActive) {
					const isThirdDemoRow =
						item.id === '4a89739c-b95d-f011-bec1-7c1e526496b8' ||
						item.ta_benefitsverificationid ===
						'4a89739c-b95d-f011-bec1-7c1e526496b8';
					if (isThirdDemoRow) {
						return true;
					}
				}
				return filters?.status === 'Order Placed'
					? false
					: shouldShowAction({
						item,
						commissionableProducts,
						actionLabel: 'Order Now',
						isSubRow,
						clinicProducts: clinicProductsMap,
					});
			},
		},
		{
			label: 'Reorder',
			icon: <TiDocumentText />,
			actionFn: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					mockedActions.handleReorder(item);
					return;
				}

				const patient = item.patient as unknown as {
					fullname: string;
					contactid: string;
				};
				const auth = item.auth as unknown as {
					ta_name: string;
					ta_priorauthid: string;
				};
				trackCoreClick('reorder_clicked', {
					source_module: 'benefits_verification',
					bv_id:
						(item.ta_benefitsverificationid as string) || (item.id as string),
					patient_id: patient.contactid,
					prior_auth_id: auth.ta_priorauthid,
				});
				setSelectedBV({
					action: 'bvCreateOrder',
					data: {
						patient: {
							label: patient.fullname,
							value: patient.contactid,
						} as SelectItem,
						auth: {
							label: auth.ta_name,
							value: auth.ta_priorauthid,
						} as SelectItem,
					},
				});
			},
			show: (item: ColumnData) => {
				// Check permission first
				if (!hasPermission(['Orders:Create'])) {
					return false;
				}
				return filters?.status === 'Approved'
					? false
					: shouldShowAction({
						item,
						commissionableProducts,
						actionLabel: 'Reorder',
						isSubRow,
						clinicProducts: clinicProductsMap,
					});
			},
		},
	], [commissionableProducts, filters?.status, hasPermission, clinicProductsMap, isDemoBVTutorialActive, mockedActions, userPort, userRole]);

	const actions = [
		{
			label: 'Edit',
			icon: <MdEdit />,
			actionFn: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					mockedActions.handleEdit(item);
					return;
				}

				trackCoreClick('edit_bv_clicked', {
					source_module: 'benefits_verification_table',
					bv_id:
						(item.ta_benefitsverificationid as string) || (item.id as string),
				});
				setModalInformation({
					mode: 'editBV',
					data: item.id as string,
					size: 'xl',
				});
			},
			show: (item: ColumnData) =>
				hasPermission(['BenefitVerification:Edit']) &&
				shouldShowAction({ item, commissionableProducts, actionLabel: 'Edit' },),
		},
		{
			label: 'Details',
			icon: <IoEyeOutline />,
			actionFn: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					mockedActions.handleDetails(item);
					return;
				}

				const bvId =
					(item.ta_benefitsverificationid as string) || (item.id as string);
				trackCoreClick('view_bv_details_clicked', {
					source_module: 'benefits_verification_table',
					bv_id: bvId,
				});
				setSelectedBV({ action: 'details', data: bvId });
				if (bvId) {
					getBVDetails(bvId);
				}
			},
			show: (item: ColumnData) => true,
			// shouldShowAction({
			// 	item,
			// 	commissionableProducts,
			// 	actionLabel: 'Details',
			// }),
		},
		{
			label: 'View PR Document',
			icon: <IoEyeOutline />,
			actionFn: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					mockedActions.handleViewPRDocument(item);
					return;
				}

				const bvId =
					(item.ta_benefitsverificationid as string) || (item.id as string);
				if (bvId) {
					trackCoreClick('view_pr_document_clicked', {
						source_module: 'benefits_verification_table',
						bv_id: bvId,
						document_type: 'pr_document',
					});
					setSelectedBV({ action: 'prDocument', data: bvId });
				} else {
					toast.error('PR document not available');
				}
			},
			show: (item: ColumnData) =>
				shouldShowAction({
					item,
					commissionableProducts,
					actionLabel: 'View PR Document',
					clinicProducts: clinicProductsMap,
				}),
		},
		...getCommonActions(false),
		{
			label: 'View Document',
			icon: <IoMdDocument />,
			show: (item: ColumnData) => !!item.ta_portalbvupload,
			actionFn: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					mockedActions.handleViewDocument(item);
					return;
				}

				trackCoreClick('view_bv_document_clicked', {
					source_module: 'benefits_verification_table',
					bv_id:
						(item.ta_benefitsverificationid as string) || (item.id as string),
					document_name: extractDocumentName(String(item.ta_portalbvupload || '')),
				});
				navigateToDoc(item);
			},
		},
		{
			label: 'Support Ticket',
			icon: <MdSupportAgent />,
			actionFn: (item: ColumnData) => {
				if (isDemoBVTutorialActive) {
					mockedActions.handleSupportTicket(item);
					return;
				}

				const clinic = item.clinic as SelectItem;
				// Find the original data to get ta_bvidfull
				const originalItem = data?.items?.bvData?.find(
					(bv: any) => bv.ta_benefitsverificationid === item.id,
				);
				const bvIdFull = originalItem?.ta_bvidfull || '';

				setIsSupportTicketFormDirty(false);
				setSelectedBV({
					action: 'supportTicket',
					data: {
						clinic: clinic,
					},
					bvIdFull: bvIdFull,
				});
			},
			show: () => hasPermission(['SupportTickets:Create']),
		},
	];

	const subActions = useCallback((item: ColumnData) => {
		const actions = STATUS_SUB_ACTIONS_MAP[item.status as string] || [];
		const filteredActions = actions.filter((action) =>
			shouldShowAction({
				item,
				commissionableProducts,
				actionLabel: action,
				isSubRow: true,
				clinicProducts: clinicProductsMap,
			}),
		);
		if (filteredActions.length === 0) {
			return null;
		}
		// Only keep menu open for the first demo row (ID: 3825bf48-2dd1-f011-bbd2-7c1e526496b7)
		const isFirstDemoRow =
			shouldOpenFirstRowMenu &&
			(item.id === '3825bf48-2dd1-f011-bbd2-7c1e526496b7' ||
				item.ta_benefitsverificationid ===
				'3825bf48-2dd1-f011-bbd2-7c1e526496b7');
		// Or for the second demo row (Approved, ID: 3f78728b-a95d-f011-bec1-7c1e526496b7)
		const isSecondDemoRow =
			shouldOpenSecondRowMenu &&
			(item.id === '3f78728b-a95d-f011-bec1-7c1e526496b7' ||
				item.ta_benefitsverificationid ===
				'3f78728b-a95d-f011-bec1-7c1e526496b7');
		return (
			<Box>
				<DataTableActionMenu
					item={item as unknown as ColumnData}
					actions={getCommonActions(true)}
					openOverride={isFirstDemoRow || isSecondDemoRow}
					trigger={
						<Box display="flex" justifyContent="center" cursor="pointer" ml={3}>
							<RxHamburgerMenu size={22} />
						</Box>
					}
				/>
			</Box>
		);
	}, [commissionableProducts, clinicProductsMap, getCommonActions, shouldOpenFirstRowMenu, shouldOpenSecondRowMenu]);
	// Transform data to table format (API or mock, depending on tutorial state)
	const transformedData = useMemo(() => {
		const statuses = getBvStatuses();

		// Helper to map a real BV record to table format
		const mapRecordToRow = (item: BenefitVerification): ColumnData => {
			// Calculate status - handle null/undefined/empty first
			let status;
			if (!item?.bv_product_status) {
				status = getBVStatus(null);
			} else {
				// If a status filter is applied, use that
				if (filters?.status) {
					status = getBVStatus(filters.status as string);
				} else {
					// Get all product statuses (excluding Void and Order Placed)
					const productStatuses = item.bv_product_status
						?.split(',')
						.filter((el) => el !== 'Void' && el !== 'Order Placed') || [];
					
					// Get unique status labels by mapping status codes to their labels
					const uniqueStatusLabels = new Set(
						item.priorAuths
							.map((auth) => {
								const statusCode = String(auth.ta_pa_status);
								return statuses[statusCode as keyof typeof statuses];
							})
							.filter(Boolean)
					);

					const missingDataStatusCode = Object.entries(statuses)
						.find(([, value]) => value.includes('Pending Clinic Response'))
						?.at(0);

					// Determine the appropriate status
					let finalStatus: string;
					
					// Check for single product with missing data
					if (
						item.priorAuths.length === 1 &&
						item.priorAuths.at(0)?.ta_pa_status === Number(missingDataStatusCode)
					) {
						finalStatus = 'Pending Clinic Response (Missing Data)';
					}
					// If all products have the same status label, use that status
					else if (uniqueStatusLabels.size === 1) {
						finalStatus = Array.from(uniqueStatusLabels)[0];
					}
					// If only one product, use its status
					else if (item.bv_product_status?.split(',')?.length === 1) {
						finalStatus = item.bv_product_status;
					}
					// Multiple products with different statuses
					else if (uniqueStatusLabels.size > 1) {
						finalStatus = 'Mixed';
					}
					// Fallback
					else {
						finalStatus = productStatuses[0] || 'In Process';
					}

					status = getBVStatus(finalStatus);
				}
			}
			return {
				ta_benefitsverificationid: item.ta_benefitsverificationid,
				id: item.ta_benefitsverificationid,
				clinic: {
					label: item.account?.name,
					value: item.account?.accountid,
				},
				'account.name': (
					<FaceliftTruncatedTextWithTooltip
						text={item.account?.name || '-'}
						maxLength={30}
					/>
				),
				'provider.fullname': (
					<FaceliftTruncatedTextWithTooltip
						text={item.provider?.fullname || '-'}
						maxLength={20}
					/>
				),
				'patient.fullname': (
					<FaceliftTruncatedTextWithTooltip
						text={item.patient?.fullname || '-'}
						maxLength={20}
					/>
				),
				'patient.primaryInsurance.ta_name':
					item.patient?.primaryInsurance?.ta_name || '',
				'patient.secondaryInsurance.ta_name':
					item.patient?.secondaryInsurance?.ta_name || '',
				ta_proceduredate: item.ta_proceduredate
					? formatDate(item.ta_proceduredate)
					: '-',
				'priorAuths.ta_name': (
					<FaceliftTruncatedTextWithTooltip
						text={getProductDisplay(item.priorAuths)}
						maxLength={25}
					/>
				),
				auth: item.priorAuths?.at(0) as unknown as ReactNode,
				patient: item.patient as unknown as ReactNode,
				bvProductStatus: item.bv_product_status,
				createdon: item.createdon ? formatDate(item.createdon) : '-',
				// Check if an order has been placed for the first product
				// ta_SelectedPriorAuth@odata.bind might be in format: /ta_priorauths(guid) or just guid or empty string
				// Only set orderRequest if the field exists, is not empty, and contains the current product's ID
				orderRequest: (() => {
					const selectedPriorAuth = item['ta_SelectedPriorAuth@odata.bind'];
					const firstProductId = item.priorAuths?.at(0)?.ta_priorauthid;
					if (selectedPriorAuth && selectedPriorAuth.trim() !== '' && firstProductId && selectedPriorAuth.includes(firstProductId)) {
						return { ta_priorauthid: firstProductId } as any;
					}
					return undefined as any;
				})(),
				subRows:
					item.priorAuths?.length > 1
						? item.priorAuths?.map((auth) => ({
							id: item.ta_benefitsverificationid,
							bvProductStatus: item.bv_product_status,
							'priorAuths.ta_name': (
								<FaceliftTruncatedTextWithTooltip
									text={auth?.ta_name}
									maxLength={25}
								/>
							),
							bv_product_status:
								statuses[
								(auth?.ta_pa_status != null
									? String(auth.ta_pa_status)
									: '') as keyof typeof statuses
								] || '-',
							action: subActions({
								...item,
								auth,
								patient: item.patient,
								'patient.primaryInsurance.ta_name':
									item.patient?.primaryInsurance?.ta_name || '',
								'patient.secondaryInsurance.ta_name':
									item.patient?.secondaryInsurance?.ta_name || '',
								ta_benefitsverificationid: item.ta_benefitsverificationid,
								id: item.ta_benefitsverificationid,
								status:
									statuses[
									(auth?.ta_pa_status != null
										? String(auth.ta_pa_status)
										: '') as keyof typeof statuses
									],
								// Check if an order has been placed for this specific product
								// ta_SelectedPriorAuth@odata.bind might be in format: /ta_priorauths(guid) or just guid or empty string
								// Only set orderRequest if the field exists, is not empty, and contains the current product's ID
								orderRequest: (() => {
									const selectedPriorAuth = item['ta_SelectedPriorAuth@odata.bind'];
									const productId = auth.ta_priorauthid;
									if (selectedPriorAuth && selectedPriorAuth.trim() !== '' && productId && selectedPriorAuth.includes(productId)) {
										return { ta_priorauthid: productId } as any;
									}
									return undefined as any;
								})(),
							} as unknown as ColumnData),
						}))
						: [],
				ta_portalbvupload: item.ta_portalbvupload,
				status: status.label,
				bv_product_status: (
					<Box
						bg={status.color}
						borderRadius={4}
						color="white"
						height="24px"
						px={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight={500}
						fontSize="sm"
					>
						<FaceliftTruncatedTextWithTooltip
							text={status.label}
							maxLength={20}
						/>
					</Box>
				),
			};
		};

		// If the DEMO tutorial is active, ignore real API data
		// and use only mocked rows to avoid loading and data-dependency issues.
		// Using real data structure from reference object for consistency.
		if (isDemoBVTutorialActive) {
			const mockData = generateMockBVData(10);
			return mockData.map(mapRecordToRow);
		}

		if (!data?.items) return [];
		const baseRows = data.items.bvData.map(mapRecordToRow);
		return baseRows;
	}, [data, isDemoBVTutorialActive, filters.status, clinicProductsMap, commissionableProducts, subActions]);

	const transformedDataToExport = (data: BenefitVerification[]) => {
		if (!data) return [];
		const statuses = getBvStatuses();

		return data.map((item) => {
			// Calculate status - handle null/undefined/empty first
			let status;
			if (!item?.bv_product_status) {
				status = getBVStatus(null);
			} else {
				// If a status filter is applied, use that
				if (filters?.status) {
					status = getBVStatus(filters.status as string);
				} else {
					// Get all product statuses (excluding Void and Order Placed)
					const productStatuses = item.bv_product_status
						?.split(',')
						.filter((el) => el !== 'Void' && el !== 'Order Placed') || [];
					
					// Get unique status labels by mapping status codes to their labels
					const uniqueStatusLabels = new Set(
						item.priorAuths
							.map((auth) => {
								const statusCode = String(auth.ta_pa_status);
								return statuses[statusCode as keyof typeof statuses];
							})
							.filter(Boolean)
					);

					const missingDataStatusCode = Object.entries(statuses)
						.find(([, value]) => value.includes('Pending Clinic Response'))
						?.at(0);

					// Determine the appropriate status
					let finalStatus: string;
					
					// Check for single product with missing data
					if (
						item.priorAuths.length === 1 &&
						item.priorAuths.at(0)?.ta_pa_status === Number(missingDataStatusCode)
					) {
						finalStatus = 'Pending Clinic Response (Missing Data)';
					}
					// If all products have the same status label, use that status
					else if (uniqueStatusLabels.size === 1) {
						finalStatus = Array.from(uniqueStatusLabels)[0];
					}
					// If only one product, use its status
					else if (item.bv_product_status?.split(',')?.length === 1) {
						finalStatus = item.bv_product_status;
					}
					// Multiple products with different statuses
					else if (uniqueStatusLabels.size > 1) {
						finalStatus = 'Mixed';
					}
					// Fallback
					else {
						finalStatus = productStatuses[0] || 'In Process';
					}

					status = getBVStatus(finalStatus);
				}
			}
			return {
				ta_benefitsverificationid: item.ta_benefitsverificationid,
				id: item.ta_benefitsverificationid,
				'account.name': item.account?.name || '-',
				'provider.fullname': item.provider?.fullname || '-',
				'patient.fullname': item.patient?.fullname || '-',
				ta_proceduredate: item.ta_proceduredate
					? formatDate(item.ta_proceduredate)
					: '-',
				'priorAuths.ta_name': getProductDisplay(item.priorAuths),
				ta_portalbvupload: extractDocumentName(item.ta_portalbvupload),
				createdon: item.createdon ? formatDate(item.createdon) : '-',
				bv_product_status: status.label,
				subRows:
					item.priorAuths?.length > 1
						? item.priorAuths?.map((auth) => ({
							id: item.ta_benefitsverificationid,
							ta_benefitsverificationid: '',
							'account.name': '',
							'provider.fullname': '',
							'patient.fullname': '',
							ta_proceduredate: '',
							'priorAuths.ta_name': auth?.ta_name || '-',
							ta_portalbvupload: '',
							createdon: '',
							bv_product_status:
								statuses[
								(auth?.ta_pa_status != null
									? String(auth.ta_pa_status)
									: '') as keyof typeof statuses
								] || '-',
						}))
						: [],
			};
		});
	};

	// Handle filter apply
	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {
		const mappedFilters = mapGlobalFilters(globalFilters || []);

		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(columnsFilters),
			search,
			enabled: defaultClinicIds.length > 0,
			status: status?.value,
			globalFilters: JSON.stringify(mappedFilters),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	const handleVoidBVConfirm = async () => {
		if (selectedBV.action === 'void' && selectedBV.data) {
			try {
				trackCoreClick('confirm_void_bv_clicked', {
					source_module: 'benefits_verification_table',
					bv_id: selectedBV.data as string,
				});
				await voidBV(selectedBV.data as string);
			} catch (error) {
				console.error('Error voiding benefit verification:', error);
			}
		}
	};

	const handleCloseModal = () => {
		setSelectedBV({ action: null, data: null });
	};

	// Export functions
	const exportAllAsCSV = async () => {
		try {
			// Fetch all data with current filters
			const data = await getBenefitVerifications(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(
				data?.items?.bvData || [],
			);
			generateCSVFromTable(
				columns,
				transformedData,
				`BenefitVerifications-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	const exportAllAsExcel = async () => {
		try {
			// Fetch all data with current filters
			const data = await getBenefitVerifications(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(data?.items.bvData || []);
			generateExcelFromTable(
				columns,
				transformedData,
				`BenefitVerifications-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	return (
		<Box
			p={{ base: 4, md: 8 }}
			py={{ base: 4, md: 6 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
		>
			<DataTable
				tableName="Benefits Verification"
				moduleName="BV"
				ref={benefitVerificationTableRef}
				addButtonRef={addButtonBenefitVerificationTableRef}
				exportButtonRef={exportButtonBenefitVerificationTableRef}
				loading={(isFetching || isLoadingProductsWithSizesByClinics) && !isDemoBVTutorialActive}
				items={transformedData as ColumnData[]}
				columns={columns}
				topLabel="Benefits Verification"
				totalCount={data?.meta.totalRecords || 0}
				onFilterApply={onFilterApply}
				requiredColumnsForExpand={['priorAuths.ta_name']}
				bordered
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				exportable
				actionMenuOpenOverride={(_item, index) => {
					// First row (index 0): open when shouldOpenFirstRowMenu is true
					if (index === 0) {
						return shouldOpenFirstRowMenu;
					}
					// Second row (index 1): open when shouldOpenSecondRowMenu is true (IVR + Order Now)
					if (index === 1) {
						return shouldOpenSecondRowMenu;
					}
					return false;
				}}
				globalFilter={{
					title: 'Filters',
					clinics: true,
					items: [
						{
							name: 'Products',
							key: 'priorAuths.ta_name',
							options: (products || [])?.filter(Boolean).map((product) => ({
								id: product,
								label: product,
							})),
						},
					],
				}}
				{...(hasPermission(['BenefitVerification:Create']) && {
					creatableFn: () => {
						trackCoreClick('create_bv_clicked', {
							source_module: 'benefits_verification_table',
						});
						setModalInformation({ mode: 'createBV', data: null, size: 'xl' });
					},
				})}
				statusFilters={[{ label: 'All', value: '' }, ...statusesFilterOptions]}
				actions={actions}
				csv
				excel
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
			/>

			<FaceliftDialogWithExitConfirmation
				showBackBtn
				title={
					!modalInformation?.step || modalInformation?.step !== 6
						? 'Benefit Verification'
						: 'Benefit Verification Review and Confirm'
				}
				open={['createBV', 'editBV'].includes(modalInformation?.mode || '')}
				onOpenChange={(openState) => {
					if (!openState) {
						setModalInformation(null);
					}
				}}
				onInteractOutside={(e: { detail?: { originalEvent?: { target?: EventTarget | null } }; preventDefault: () => void }) => {
					const target = e?.detail?.originalEvent
						?.target as HTMLElement | undefined;
					if (target?.closest('[data-facelift-date-picker-portal]')) {
						e.preventDefault();
					}
				}}
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					...(modalInformation?.size === 'fullwidth' && {
						width: '95vw',
						maxWidth: '95vw',
					}),
				}}
				size={modalInformation?.size || 'xl'}
				isDirty={isFormDirty}
				saveDraftAction={benefitVerificationExitDraftAction}
			>
				<BenefitVerificationForm
					customFunction={(data) => {
						setModalInformation((prev) => ({
							mode: prev?.mode || '',
							data: prev?.data || null,
							size: (data?.size as string) || 'xl',
							step: data?.step,
						}));
					}}
					onSuccess={() => {
						setModalInformation(null);
						refetch();
					}}
					id={modalInformation?.data}
					onIsDirtyChange={setIsFormDirty}
					onExitConfirmationDraftActionChange={
						['createBV', 'editBV'].includes(modalInformation?.mode || '')
							? setBenefitVerificationExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>

			{/* IVR Document Modal */}
			<FaceliftDialog
				open={selectedBV.action === 'ivr' && !isDemoBVTutorialActive}
				showBackBtn
				onOpenChange={(openState) => {
					if (!openState) {
						handleCloseModal();
					}
				}}
				title="IVR Document"
				sx={{
					width: 900,
					maxWidth: '95vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'visible',
					overflowY: 'auto',
				}}
			>
				<IVRViewModal
					data={selectedBV.data as IVRViewModalData}
					commissionableProducts={commissionableProducts || []}
				/>
			</FaceliftDialog>

			{/* Demo IVR Document Modal for Tutorial */}
			<FaceliftDialog
				open={isDemoIVRModalOpen}
				showBackBtn={true}
				onOpenChange={(openState) => {
					// Keep modal open during tutorial unless explicitly closed
					if (!openState && isDemoBVTutorialActive) {
						return;
					}
					setIsDemoIVRModalOpen(openState);
				}}
				title="IVR Document"
				sx={{
					width: 900,
					maxWidth: '95vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'visible',
					overflowY: 'auto',
				}}
			>
				<DemoIVRViewModal />
			</FaceliftDialog>

			{/* Demo Order Now Modal */}
			<FaceliftDialog
				open={isDemoOrderNowModalOpen}
				showBackBtn={true}
				onOpenChange={(openState) => {
					// Keep modal open during tutorial unless explicitly closed
					if (!openState && isDemoBVTutorialActive) {
						return;
					}
					setIsDemoOrderNowModalOpen(openState);
				}}
				title="Create Patient Order"
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					overflowY: 'auto',
				}}
			>
				<DemoOrderRequestForm />
			</FaceliftDialog>

			{/* PR Document View Modal */}
			<FaceliftDialog
				open={selectedBV.action === 'prDocument'}
				showBackBtn
				onOpenChange={(openState) => {
					if (!openState) {
						handleCloseModal();
					}
				}}
				title="Preliminary Verification Document"
				sx={{
					width: 900,
					maxWidth: '95vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'visible',
					overflowY: 'auto',
				}}
			>
				<PRDocumentViewModal
					benefitVerificationId={selectedBV.data as string}
				/>
			</FaceliftDialog>

			{/* Void BV Confirmation Modal */}
			<FaceliftDialog
				open={selectedBV.action === 'void'}
				onOpenChange={(openState) => {
					if (!openState) {
						handleCloseModal();
					}
				}}
				title="Void Benefit Verification"
				subtitle="Please confirm these changes. This action cannot be undone."
				sx={{ boxShadow: `0 0 20px 0px ${brand600}` }}
				size="md"
				footer={
					<>
						<FaceliftButton
							onClick={handleCloseModal}
							loading={isVoidingBV}
							disabled={isVoidingBV}
							bg="transparent"
							color="inherit"
							border="1px solid"
							width="20%"
						>
							Cancel
						</FaceliftButton>
						<FaceliftButton
							onClick={handleVoidBVConfirm}
							loading={isVoidingBV}
							disabled={isVoidingBV}
							width="20%"
						>
							Confirm
						</FaceliftButton>
					</>
				}
			/>
			<FaceliftDialog
				open={selectedBV.action === 'bvCreateOrder'}
				onOpenChange={(openState) => {
					if (!openState) {
						setSelectedBV({ action: null, data: null });
					}
				}}
				title="Create Patient Order"
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					// height: '565px',
					// maxHeight: '90vh',
					overflowY: 'auto',
				}}
			>
				<OrderRequestForm
					mode="bvCreateOrder"
					data={selectedBV.data as Record<string, SelectItem>}
					onSuccess={() => {
						setSelectedBV({ action: null, data: null });
						setModalInformation(null);
						refetch();
					}}
				/>
			</FaceliftDialog>
			<FaceliftDialog
				open={selectedBV.action === 'details'}
				onOpenChange={(openState) => {
					if (!openState) {
						setSelectedBV({ action: null, data: null });
					}
				}}
				title="Details"
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '90vw',
					maxWidth: '95vw',
					maxHeight: '90vh',
					overflowY: 'auto',
				}}
			>
				<Show when={isLoadingBVDetails}>
					<FaceliftDataLoader
						title="Loading details..."
						description="Please wait while we fetch the benefit verification details."
					/>
				</Show>
				<Show when={!isLoadingBVDetails && !!bvDetails && !!accountProducts}>
					<Preview
						bvInformation={transformBVRecordToPreview(
							bvDetails || null,
							accountProducts,
						)}
					/>
				</Show>
			</FaceliftDialog>

			{/* Support Ticket Modal */}
			<FaceliftDialogWithExitConfirmation
				open={selectedBV.action === 'supportTicket'}
				onOpenChange={(openState) => {
					if (!openState) {
						setSelectedBV({ action: null, data: null });
						setIsSupportTicketFormDirty(false);
					}
				}}
				title="Create Support Ticket"
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					overflowY: 'auto',
				}}
				isDirty={isSupportTicketFormDirty}
				saveDraftAction={supportTicketExitDraftAction}
			>
				<SupportTicketForm
					mode="create"
					initialData={{
						clinic:
							(selectedBV.data as Record<string, SelectItem>)?.clinic || null,
						type: { value: '953870000', label: 'Benefits Verification' },
						subject: selectedBV.bvIdFull || '',
						status: { value: '1', label: 'In Progress' },
					}}
					onSuccess={() => {
						setSelectedBV({ action: null, data: null });
						setIsSupportTicketFormDirty(false);
						refetch();
					}}
					onIsDirtyChange={setIsSupportTicketFormDirty}
					onExitConfirmationDraftActionChange={
						selectedBV.action === 'supportTicket'
							? setSupportTicketExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>
			{/* Demo-only modal for Benefit Verification tutorial */}
			<FaceliftDialog
				open={isDemoModalOpen}
				onOpenChange={(openState) => {
					// Keep demo modal open while the BenefitVerificationTutorialWC is active
					if (!openState && isDemoBVTutorialActive) {
						return;
					}
					setIsDemoModalOpen(openState);
				}}
				showBackBtn={true}
				title="Benefit Verification"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					maxHeight: '90vh',
					overflowY: 'auto',
				}}
				size="xl"
			>
				<DemoBVForm ref={demoFormRef} />
			</FaceliftDialog>
		</Box>
	);
};

export default BenefitVerificationTable;
